import type { User } from "firebase/auth";
import { onAuthStateChanged } from "firebase/auth";
import type { MemberContext } from "../security/permissions";
import { auth } from "../services/firebase";

export type PendingInvite = { clubId: string; inviteId: string };

function safeGetLS(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function safeSetLS(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {}
}
function safeRemoveLS(key: string) {
  try {
    localStorage.removeItem(key);
  } catch {}
}

const LS_ACTIVE_CLUB = "activeClubId";
const LS_ACTIVE_SEASON = "activeSeasonId";
const LS_ACTIVE_TEAM = "activeTeamId";
const SS_PENDING_INVITE = "pendingInvite";

class SessionState {
  user: User | null = null;
  member: MemberContext | null = null;

  /** True until the first onAuthStateChanged callback fires */
  loading = true;

  // Initialise from localStorage so pages can read immediately
  activeClubId: string | null = safeGetLS(LS_ACTIVE_CLUB);
  activeSeasonId: string | null = safeGetLS(LS_ACTIVE_SEASON) ?? "2025";
  activeTeamId: string | null = safeGetLS(LS_ACTIVE_TEAM) ?? "sunday";

  private bootstrapped = false;

  // ---- auth ready gate ----
  private _authReadyDone = false;
  private _authReadyResolve!: () => void;
  private _authReady = new Promise<void>((res) => (this._authReadyResolve = res));

  /**
   * Await this before making routing decisions that depend on auth.
   * (Prevents "flash to login then bounce back".)
   */
 async ready(): Promise<void> {
  // Ensure auth listener is definitely attached
  this.bootstrap();
  await this._authReady;
}


  private _markAuthReady() {
    if (this._authReadyDone) return;
    this._authReadyDone = true;
    this._authReadyResolve();
  }

  bootstrap() {
    if (this.bootstrapped) return;
    this.bootstrapped = true;

    onAuthStateChanged(auth, (user) => {
      this.user = user;
      this.loading = false;

      // Resolve the "auth ready" promise the first time we get a callback.
      this._markAuthReady();

      // IMPORTANT: do not navigate here.
      // Pages and route guards should await session.ready() and decide.
    });
  }

  // --- active club helpers ---
  setActiveClubId(clubId: string | null) {
    this.activeClubId = clubId;
    if (clubId) safeSetLS(LS_ACTIVE_CLUB, clubId);
    else safeRemoveLS(LS_ACTIVE_CLUB);
  }

  getActiveClubId(): string | null {
    return this.activeClubId ?? safeGetLS(LS_ACTIVE_CLUB);
  }

  // --- active season helpers ---
  setActiveSeasonId(seasonId: string | null) {
    this.activeSeasonId = seasonId;
    if (seasonId) safeSetLS(LS_ACTIVE_SEASON, seasonId);
    else safeRemoveLS(LS_ACTIVE_SEASON);
  }

  getActiveSeasonId(): string {
    return (this.activeSeasonId ?? safeGetLS(LS_ACTIVE_SEASON) ?? "2025") as string;
  }

  // --- active team helpers ---
  setActiveTeamId(teamId: string | null) {
    this.activeTeamId = teamId;
    if (teamId) safeSetLS(LS_ACTIVE_TEAM, teamId);
    else safeRemoveLS(LS_ACTIVE_TEAM);
  }

  getActiveTeamId(): string {
    return (this.activeTeamId ?? safeGetLS(LS_ACTIVE_TEAM) ?? "sunday") as string;
  }

  // --- pending invite helpers ---
  setPendingInvite(invite: PendingInvite) {
    try {
      sessionStorage.setItem(SS_PENDING_INVITE, JSON.stringify(invite));
    } catch {}
  }

  getPendingInvite(): PendingInvite | null {
    let raw: string | null = null;
    try {
      raw = sessionStorage.getItem(SS_PENDING_INVITE);
    } catch {
      return null;
    }
    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw);
      if (!parsed?.clubId || !parsed?.inviteId) return null;
      return { clubId: String(parsed.clubId), inviteId: String(parsed.inviteId) };
    } catch {
      return null;
    }
  }

  clearPendingInvite() {
    try {
      sessionStorage.removeItem(SS_PENDING_INVITE);
    } catch {}
  }

  // Backwards-compatible setters
  setUser(user: User | null) {
    this.user = user;
  }
  setMember(member: MemberContext | null) {
    this.member = member;
  }
  setLoading(loading: boolean) {
    this.loading = loading;
  }
}

export const session = new SessionState();
